import {
  Estimation,
  Validation,
  ValidationFilter,
  ValidationFilter_2,
  ValidationResult,
} from "./../types/estimation.type";
import { Pagination, PaginationRequest } from "./../types/pagination.type";
import axios from "./api";
import { responseHandlerDefault } from "./../utils/handler.util";
import { DataUpload } from "./../types/dataupload.type";
import { DataStation } from "@/types/datastation.type";

/**
 * report parameters
 * @param page
 * @param size
 * @param filter
 * @param timeZone
 * @returns
 */
export const getValidation = (
  page: number | undefined,
  size: number | undefined,
  filter: ValidationFilter,
  dataUpload: DataUpload[]
): Promise<Pagination<Validation>> => {
  const params: PaginationRequest<ValidationFilter> = {};
  if (page) {
    params.page = page;
  }
  if (size) {
    params.size = size;
  }
  if (filter) {
    params.filter = filter;
  }
  return axios
    .post(`/estimation`, {
      dataUpload,
      params: { ...params },
    })
    .then(responseHandlerDefault);
};
/**
 * report parameters
 * @param page
 * @param size
 * @param filter
 * @param timeZone
 * @returns
 */
export const getValidation_2 = (
  page: number | undefined,
  size: number | undefined,
  filter: ValidationFilter_2,
  estimations: Estimation[],
  dataUpload: DataUpload[]
): Promise<Pagination<Validation>> => {
  const params: PaginationRequest<ValidationFilter_2> = {};

  if (page) {
    params.page = page;
  }
  if (size) {
    params.size = size;
  }
  if (filter) {
    params.filter = filter;
  }
  return axios
    .post(`/estimation/validation2`, {
      estimations,
      dataUpload,
      params: { ...params },
    })
    .then(responseHandlerDefault);
};

export const getPaginatedEstimations = (filter: ValidationFilter_2): Promise<Estimation[]> => {
  const params: PaginationRequest<ValidationFilter_2> = {};

  if (filter) {
    params.filter = filter;
  }
  return axios
    .post(`/estimation/paginatedEstimations`, {
      params: { ...params },
    })
    .then(responseHandlerDefault);
};

export const getDatesWithEstimations = (
  samplingPointIds: number[],
  parameterIds: number[]
): Promise<{ image_date: string; image_dateTime: string }[]> => {
  return axios
    .get<Date[]>(
      `/estimation/dates?samplingPoint=[${samplingPointIds}]&parameter=[${parameterIds}]`
    )
    .then(responseHandlerDefault);
};

export const getDatesWithEstimationsGallery = (
  samplingPointIds: number[]
): Promise<{ image_date: string; image_dateTime: string }[]> => {
  return axios
    .get<Date[]>(`/estimation/datesEstimation?samplingPoint=[${samplingPointIds}]`)
    .then(responseHandlerDefault);
};

export const getDatesWithDataStation = (
  samplingPointIds: number[],
  magnitude: string[]
): Promise<DataStation[]> => {
  return axios
    .get<Date[]>(
      `/estimation/station_dates?samplingPoint=[${samplingPointIds}]&magnitude=[${magnitude}]`
    )
    .then(responseHandlerDefault);
};

/**
 * report parameters
 * @param filter
 * @param timeZone
 * @returns
 */
export const downloadReportValidation = (
  filter: ValidationFilter,
  dataValidation: DataUpload[]
): Promise<{ fileName: string; url: string }> => {
  return axios
    .post(
      `/estimation/validation`,
      {
        dataValidation,
        filter,
      },
      {
        responseType: "blob",
      }
    )
    .then((res) => {
      responseHandlerDefault(res);
      const match = res.headers["content-disposition"]?.match(/filename\s*=\s*"(.+)"/i);
      const fileName = match && match[1] ? match[1] : "report.csv";

      return { fileName, url: URL.createObjectURL(res.data) };
    });
};
/**
 * report parameters
 * @param filter
 * @param timeZone
 * @returns
 */
export const downloadReportValidation_2 = (
  filter: ValidationFilter_2,
  dataValidation: DataUpload[],
  estimations: Estimation[]
): Promise<{ fileName: string; url: string }> => {
  return axios
    .post(
      `/estimation/reportValidation`,
      {
        dataValidation,
        estimations,
        filter,
      },
      {
        responseType: "blob",
      }
    )
    .then((res) => {
      responseHandlerDefault(res);
      const match = res.headers["content-disposition"]?.match(/filename\s*=\s*"(.+)"/i);
      const fileName = match && match[1] ? match[1] : "report.csv";

      return { fileName, url: URL.createObjectURL(res.data) };
    });
};

export const getResult = (
  filter: ValidationFilter,
  dataValidation: DataUpload[]
): Promise<ValidationResult> => {
  return axios
    .post(`/estimation/result`, {
      dataValidation,
      filter,
    })
    .then(responseHandlerDefault);
};
export const getResult_2 = (
  filter: ValidationFilter,
  dataValidation: DataUpload[],
  estimations: Estimation[]
): Promise<ValidationResult> => {
  return axios
    .post(`/estimation/result_2`, {
      dataValidation,
      estimations,
      filter,
    })
    .then(responseHandlerDefault);
};

export const getImportResult = (
  filter: ValidationFilter,
  dataValidation: DataUpload[],
  estimations: Array<{ dateTime: string; value: string }>
): Promise<ValidationResult> => {
  return axios
    .post(`/estimation/import_result`, {
      dataValidation,
      estimations,
      filter,
    })
    .then(responseHandlerDefault);
};

export const getEstimationsByImageParameter = (
  imageId?: number,
  parameterIds?: number[]
): Promise<Estimation[]> => {
  const params = {
    imageId: imageId,
    parameterIds: parameterIds,
  };
  return axios
    .get(`estimation/estimationsByImageParameter`, { params })
    .then(responseHandlerDefault);
};

export const getAverageEstimations = (
  filter: ValidationFilter_2,
  timeFrom: string,
  timeTo: string
): Promise<Estimation[]> => {
  const params: { filter?: ValidationFilter_2 } = {};

  if (filter) {
    params.filter = filter;
  }
  return axios
    .post(`/estimation/averageEstimations`, {
      params: { ...params, timeFrom, timeTo },
    })
    .then((response) => response.data)
    .catch((error) => {
      console.error("Error fetching average estimations:", error);
      throw error;
    });
};

/**
 * Obtener min/max de un parámetro para cada TMOA
 * @param samplingPointId
 * @param parameterId
 * @param startDate
 * @param endDate
 * @returns
 */
export const getParameterRanges = (
  samplingPointId: number,
  parameterId: number,
  startDate?: string,
  endDate?: string
): Promise<
  {
    id: number;
    name: string | null;
    value: number | null;
    min: number | null;
    max: number | null;
  }[]
> => {
  const params = {
    samplingPointId,
    parameterId,
    startDate,
    endDate,
  };

  return axios
    .post(`/estimation/parameterRanges`, {
      params: { ...params },
    })
    .then(responseHandlerDefault);
};
